import i18n from 'common/util/internationalization'
import { LockedModal } from 'components/shared/LockedModal'
import emitter from 'common/util/events'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthentication } from 'use-authentication'
import { pathOr } from 'rambdax'
import InternationalizationProvider from 'components/shared/internationalization/InternationalizationProvider'
import {
    getLocalStorageItem,
    removeLocalStorageItem,
    removeSessionStorageItem
} from 'businesslayer/minutesLocalStore'
import { useUuid } from 'use-Uuid'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    makeStyles,
    Typography,
    Divider
} from '@material-ui/core'
import { AtlasButtonOld } from 'components/atlas-material/button'

type Props = {
    authTokens: {
        initialAuthToken: string | null
        sessionId: string | null
    }
    children: any
    uuid: string | null
    platform: any
    siteName: string | null
}
const useStyles = makeStyles((theme) => ({
    dialogContent: {
        paddingBottom: theme.spacing(2)
    },
    errorMessage: {
        marginTop: theme.spacing(2),
        minWidth: '300px',
        wrap: 'break-word'
    }
}))
export const AuthLock = ({ authTokens, uuid, platform, children }: Props) => {
    const language = getLocalStorageItem('userLanguage') || 'en-us'
    const [lockedState, setLockedState] = useState({
        isLocked: true,
        message: '',
        isErrorLocked: false
    })
    const [errorDialogOpen, setErrorDialogOpen] = useState(false)
    const [error, setErrorMessage] = useState('')
    const environmentRedirect = sessionStorage.getItem('environmentRedirect') === 'false'
    const isReauthenticatingRef = useRef(false)

    const parentPlatform = platform || transientStorageManager.platform
    if (parentPlatform) {
        if (parentPlatform === 'boardeffect') {
            removeLocalStorageItem('sessionId')
        } else {
            removeSessionStorageItem('sessionId')
        }
    }

    const { isExchanging, hasAuthToken, reauthenticate } = useAuthentication(
        authTokens,
        parentPlatform
    )
    const { errorMessage } = useUuid(uuid)

    const handleErrorControl = useCallback((message) => {
        setErrorDialogOpen(true)
        setErrorMessage(message)
    }, [])

    // On Mount
    useEffect(() => {
        const handleLocked = (e) => {
            const message = pathOr('', 'message', e)

            // Prevent multiple reauthenticate calls when multiple 401s occur simultaneously
            // (e.g., parallel API calls all returning 401)
            if (!isReauthenticatingRef.current) {
                isReauthenticatingRef.current = true
                reauthenticate()
                // Only unlock if we have auth token AND we're not error-locked
                // This prevents unlocking during re-authentication attempts after a 401
            }
            // Always update the locked state to show the latest message
            setLockedState({ isLocked: true, message, isErrorLocked: true })
        }

        document.title = i18n.t('WINDOW_TITLE')
        emitter.on('locked', handleLocked)

        // Unmount
        return () => {
            emitter.off('locked', handleLocked)
        }
    }, [reauthenticate])

    useEffect(() => {
        emitter.on('error', handleErrorControl)
        return () => emitter.off('error', handleErrorControl)
    }, [handleErrorControl])

    // Unlock App If we have an AuthToken
    useEffect(() => {
        if (hasAuthToken && !lockedState.isErrorLocked) {
            // Reset the re-authenticating flag for future re-auth attempts
            isReauthenticatingRef.current = false
            setLockedState({ isLocked: false, message: '', isErrorLocked: false })
        }
    }, [hasAuthToken, lockedState.isErrorLocked])

    if (
        errorMessage.status === 401 &&
        window.location.href !== '/not-found' &&
        !environmentRedirect
    ) {
        return (
            <InternationalizationProvider language={language}>
                <LockedModal message={errorMessage.message} />
            </InternationalizationProvider>
        )
    }

    // User is Unauthorized; Return lock screen
    if (lockedState.isLocked && window.location.href !== '/not-found' && !environmentRedirect) {
        return (
            <InternationalizationProvider language={language}>
                <LockedModal isExchanging={isExchanging} message={lockedState.message} />
            </InternationalizationProvider>
        )
    } else {
        return (
            <>
                <ErrorPopUp
                    errorDialogOpen={errorDialogOpen}
                    setErrorDialogOpen={setErrorDialogOpen}
                    error={error}
                />
                {children}
            </>
        )
    }
}

const ErrorPopUp = ({ errorDialogOpen, setErrorDialogOpen, error }) => {
    const classes = useStyles()
    return (
        <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}>
            <DialogTitle>{i18n.t('AUTOSAVE_ERROR')}</DialogTitle>
            <Divider />
            <DialogContent className={classes.dialogContent}>
                <Typography variant="body2" className={classes.errorMessage}>
                    {error}
                </Typography>
            </DialogContent>
            <Divider />
            <DialogActions>
                <AtlasButtonOld onClick={() => setErrorDialogOpen(false)}>
                    {i18n.t('CLOSE')}
                </AtlasButtonOld>
            </DialogActions>
        </Dialog>
    )
}
